import { useEffect, useState, useMemo } from 'react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Database, Plus, Loader2, Trash2, Globe, Building2 } from 'lucide-react';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import cultivarService from '../../services/cultivar';
import type { AttributeDefinition, AttributeDataType } from '../../types/cultivar';

const DATA_TYPES: AttributeDataType[] = ['number', 'text', 'enum', 'boolean', 'date', 'range'];
const VISIBILITY_DEFAULTS = ['owner', 'licensee', 'production_licensee', 'program_orchard', 'public'] as const;

const emptyForm = {
  slug: '',
  display_name: '',
  category: '',
  data_type: 'text' as AttributeDataType,
  unit: '',
  enum_options_csv: '',
  is_seasonal: false,
  visibility_default: 'production_licensee' as typeof VISIBILITY_DEFAULTS[number],
  description: '',
};

function AttributeDefinitionsManager() {
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [defs, setDefs] = useState<AttributeDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (hasAccess) load();
  }, [hasAccess]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await cultivarService.listAttributeDefinitions(false);
      if (r.success && r.data) setDefs(r.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const m = new Map<string, AttributeDefinition[]>();
    for (const d of defs) {
      const key = d.category || 'custom';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(d);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [defs]);

  const handleSubmit = async () => {
    setError(null);
    if (!form.slug || !form.display_name || !form.category) {
      setError('Slug, display name, and category are required');
      return;
    }
    if (form.data_type === 'enum' && !form.enum_options_csv.trim()) {
      setError('Enum attributes need at least one option');
      return;
    }
    setSubmitting(true);
    try {
      await cultivarService.createAttributeDefinition({
        slug: form.slug.trim(),
        display_name: form.display_name.trim(),
        category: form.category.trim(),
        data_type: form.data_type,
        unit: form.unit.trim() || undefined,
        enum_options: form.data_type === 'enum'
          ? form.enum_options_csv.split(',').map(s => s.trim()).filter(Boolean)
          : undefined,
        is_seasonal: form.is_seasonal,
        visibility_default: form.visibility_default,
        description: form.description.trim() || undefined,
      });
      setShowForm(false);
      setForm(emptyForm);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (d: AttributeDefinition) => {
    if (d.is_global) return;
    if (!window.confirm(`Deactivate "${d.display_name}"? Existing observation values keep their reference.`)) return;
    try {
      await cultivarService.deactivateAttributeDefinition(d.id);
      await load();
    } catch (e: any) {
      window.alert(e?.response?.data?.error?.message || e?.message || 'Failed');
    }
  };

  if (licLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
      </div>
    );
  }
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Attribute Definitions" icon={<Database className="h-6 w-6 text-white" />} />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-neutral-600">A Cultivar PBR or Admin licence is required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Cultivar Attribute Definitions"
        subtitle="Global definitions plus your organisation's custom attributes"
        icon={<Database className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="primary" size="sm" onClick={() => setShowForm(s => !s)}>
            <Plus className="h-4 w-4" /> {showForm ? 'Cancel' : 'New custom attribute'}
          </Button>
        }
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-4 p-3 rounded bg-error-50 border border-error-200 text-error-800 text-sm">
            {error}
          </div>
        )}

        {showForm && (
          <Card className="mb-6">
            <CardHeader><CardTitle>New attribute</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Slug (a–z, 0–9, _)" value={form.slug}
                       onChange={v => setForm({ ...form, slug: v.toLowerCase() })} placeholder="brix" />
                <Field label="Display name" value={form.display_name}
                       onChange={v => setForm({ ...form, display_name: v })} placeholder="Brix" />
                <Field label="Category" value={form.category}
                       onChange={v => setForm({ ...form, category: v })} placeholder="fruit_quality" />
                <SelectField label="Data type" value={form.data_type}
                       onChange={v => setForm({ ...form, data_type: v as AttributeDataType })}
                       options={DATA_TYPES.map(t => ({ value: t, label: t }))} />
                <Field label="Unit (optional)" value={form.unit}
                       onChange={v => setForm({ ...form, unit: v })} placeholder="°Bx, mm, g" />
                <SelectField label="Default visibility" value={form.visibility_default}
                       onChange={v => setForm({ ...form, visibility_default: v as any })}
                       options={VISIBILITY_DEFAULTS.map(v => ({ value: v, label: v }))} />
                {form.data_type === 'enum' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Enum options (comma separated)
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
                      value={form.enum_options_csv}
                      onChange={e => setForm({ ...form, enum_options_csv: e.target.value })}
                      placeholder="clingstone, semi_freestone, freestone"
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.is_seasonal}
                           onChange={e => setForm({ ...form, is_seasonal: e.target.checked })} />
                    Captured per season (allows season_year on observations)
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Description (optional)</label>
                  <textarea
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
                    rows={2}
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                  Create
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-primary-600 animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            {grouped.map(([category, items]) => (
              <Card key={category}>
                <CardHeader>
                  <CardTitle className="text-base capitalize">{category.replace(/_/g, ' ')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="divide-y divide-neutral-200">
                    {items.map(d => (
                      <div key={d.id} className="py-2 flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-neutral-900">{d.display_name}</span>
                            <code className="text-xs text-neutral-500">{d.slug}</code>
                            <span className="text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
                              {d.data_type}{d.unit ? ` · ${d.unit}` : ''}
                            </span>
                            {d.is_global ? (
                              <span className="inline-flex items-center gap-1 text-xs text-blue-700">
                                <Globe className="h-3 w-3" /> global
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                                <Building2 className="h-3 w-3" /> custom
                              </span>
                            )}
                            {d.is_seasonal && <span className="text-xs text-neutral-500">seasonal</span>}
                          </div>
                          {d.description && <p className="text-xs text-neutral-600 mt-1">{d.description}</p>}
                          {d.data_type === 'enum' && d.enum_options && (
                            <p className="text-xs text-neutral-500 mt-1">
                              Options: {d.enum_options.join(', ')}
                            </p>
                          )}
                        </div>
                        {!d.is_global && (
                          <button
                            className="text-neutral-400 hover:text-error-600"
                            onClick={() => handleDeactivate(d)}
                            title="Deactivate"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      <input
        className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      <select
        className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm bg-white"
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export default AttributeDefinitionsManager;
